/**
 * Local best-time persistence. Phase 1 stores times locally only; there is
 * no server and no leaderboard. Keys include the level version and engine
 * movement-config hash so times from different physics are never mixed.
 */
export interface RecordStore {
  best(key: string): number | null;
  /** Saves `seconds` if it beats the stored best. Returns true when it is a new best. */
  submit(key: string, seconds: number): boolean;
}

export class MemoryRecordStore implements RecordStore {
  private readonly map = new Map<string, number>();

  best(key: string): number | null {
    return this.map.get(key) ?? null;
  }

  submit(key: string, seconds: number): boolean {
    const prev = this.map.get(key);
    if (prev !== undefined && prev <= seconds) return false;
    this.map.set(key, seconds);
    return true;
  }
}

export class LocalRecordStore implements RecordStore {
  private readonly fallback = new MemoryRecordStore();

  constructor(private readonly prefix = 'xernous.best.') {}

  best(key: string): number | null {
    try {
      const raw = globalThis.localStorage?.getItem(this.prefix + key);
      const v = raw === null || raw === undefined ? NaN : Number(raw);
      return Number.isFinite(v) && v > 0 ? v : this.fallback.best(key);
    } catch (err: unknown) {
      console.warn('LocalRecordStore: storage unavailable, using memory', err);
      return this.fallback.best(key);
    }
  }

  submit(key: string, seconds: number): boolean {
    const prev = this.best(key);
    if (prev !== null && prev <= seconds) return false;
    this.fallback.submit(key, seconds);
    try {
      globalThis.localStorage?.setItem(this.prefix + key, String(seconds));
    } catch (err: unknown) {
      console.warn('LocalRecordStore: could not persist best time', err);
    }
    return true;
  }
}
